"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight, PackageMinus, Search } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TRANSITIONS = [
  { from: "Draft", to: "Sent, Cancelled" },
  {
    from: "Sent, Partially Paid, Paid, Overdue",
    to: "Cancelled only. You never Mark As Paid, Partially Paid, or Overdue — payments, credit notes, and due dates set those",
  },
  {
    from: "Cancelled",
    to: "Nothing. Mark As is hidden. Cancelled is a dead end",
  },
];

const CONVERT_FROM = [
  {
    from: "Quote",
    when: "Accepted only. Convert To Invoice sits on the quote itself",
    result:
      "Customer, items, amounts, charges, and notes come across. The quote stays Accepted until you save this invoice — then it becomes Completed. One quote becomes a Sales Order or an Invoice, never both",
  },
  {
    from: "Sales Order",
    when: "Open or Partially Recorded. Convert to Invoice on the order row, detail, or the Sales Order list CONVERT menu",
    result:
      "Remaining quantity comes across. Saving this invoice moves the Sales Order to Partially Recorded, or Completed if remaining quantity hits zero",
  },
  {
    from: "Delivery Challan",
    when: "Sent only. Convert To Invoice on the challan, or the Delivery Challan list CONVERT menu",
    result: "Dispatched lines come across",
  },
  {
    from: "Scratch",
    when: "Add Invoice with nothing pre-filled",
    result: "A new invoice. Save makes Draft; Save & Sent makes Sent",
  },
  {
    from: "Duplicate",
    when: "Duplicate Document on the list or the detail page",
    result:
      "Opens Add with the same customer, items, and amounts. New invoice number, today's dates, Draft, no attachments, no Quote / Sales Order / Challan link, and no e-invoice or e-way bill",
  },
];

const CONVERT_TO = [
  {
    to: "Payment Received",
    when: "Balance is greater than zero. The payment picker lists Sent, Partially Paid, or Overdue — not Draft",
    where: "Payments Received → Add. There is no Convert button on the invoice",
    result:
      "Approving the payment moves this invoice to Partially Paid or Paid. You do not Mark As those statuses yourself",
  },
  {
    to: "Apply Credit",
    when: "This invoice still has a balance, and the customer's Opening Balance on this invoice's branch is negative (remaining credit). The button is on the invoice detail",
    where: "Apply Credit on the invoice detail. Modal title: Apply Opening Balance to Invoices",
    result:
      "This invoice is pre-selected. You enter how much remaining credit to apply. It becomes Partially Paid or Paid without creating a Payment Received. The mapping shows on Payment History as Opening Balance — Credit",
  },
  {
    to: "Credit Note",
    when: "A credit note can name this invoice, or be applied to it later",
    where: "Credit Notes module — not a Convert button on the invoice",
    result:
      "Reduces what is still due. A credit note on this invoice blocks cancel (if not cancelled) and delete (even if cancelled)",
  },
  {
    to: "Dispatch",
    when: "Any status except Cancelled",
    where: "Dispatch on the list row or the detail page — transport details, not a new sales document",
    result:
      "Stores vehicle / transporter information used on the PDF and e-way bill. A dispatch blocks cancel and delete",
  },
];

const RULES = [
  {
    topic: "Ledger posting",
    rule: "An invoice posts to the general ledger as soon as it is saved — including Draft. Cancelled reverses that journal. Moving Sent → Partially Paid → Paid does not re-post the invoice journal",
  },
  {
    topic: "Subscription gate",
    rule: "Creating an invoice is limited by your plan. E-Invoice / E-Way Bill are separate plan features. Editing, status, duplicating, dispatch, and deleting are not separately gated",
  },
  {
    topic: "A primary bank account is required to create",
    rule: "If your company hasn't set up a primary bank account yet, you can't create an invoice at all — mark one under Banking → Banks with Make this primary. The bank details print on the PDF",
  },
  {
    topic: "Invoice number",
    rule: "Auto-generated with an INV prefix unless you turn that off under Invoice Configuration on the form. That same drawer also stores Terms & Conditions. Locked after save",
  },
  {
    topic: "Division, Branch, Warehouse, Customer",
    rule: "All four are required to create. After save they are locked. Converted-from Quote, Sales Order, or Delivery Challan also lock those fields on the new form before save",
  },
  {
    topic: "Currency",
    rule: "Taken from the customer at creation, or from the Quote / Sales Order / Delivery Challan if this invoice came from one. An exchange rate field appears when that currency differs from the company base",
  },
  {
    topic: "Stock Deduct Source is a company setting",
    rule: "Settings → Inventory Config → Stock Deduct Source. Set it to Invoice for this module to take warehouse quantity. The choice is stored on each invoice at create time; later setting changes do not rewrite old invoices",
  },
  {
    topic: "Stock check vs stock deduct on this invoice",
    rule: "Stock Check Source can be Invoice (availability only). Stock Deduct Source = Invoice takes warehouse quantity on save, including a Draft. Cancel or delete (if not already Cancelled) puts deducted quantity back. Inventory Impact on detail shows the snapshot from create time",
  },
  {
    topic: "You can only edit a Draft",
    rule: "Sent, Partially Paid, Paid, Overdue, and Cancelled cannot be edited. Customer and location stay locked even on a Draft",
  },
  {
    topic: "Editing skips the overdue-invoice check",
    rule: "That check only runs when you first create an invoice. Editing a Draft re-checks address, credit limit, and stock, but not overdue invoices",
  },
  {
    topic: "Paid / Partially Paid / Overdue are not Mark As",
    rule: "Mark Invoice As on Draft offers Sent and Cancelled. On every other live status it only offers Cancelled. Payments and credit notes set Paid / Partially Paid. Overdue follows the due date",
  },
  {
    topic: "Cancelled is a dead end",
    rule: "Unlike Quotes, Sales Orders, and Delivery Challans, a Cancelled invoice cannot be brought back to Draft or Sent",
  },
  {
    topic: "Cancelling vs deleting",
    rule: "Cancel is blocked by a non-cancelled Payment Received, a non-cancelled Credit Note, or a Dispatch. Delete is stricter — blocked by any Payment Received, any Credit Note, or a Dispatch, including cancelled payments and credit notes. Opening-balance credit on the invoice must be removed before delete",
  },
  {
    topic: "Source documents after cancel / delete",
    rule: "If this invoice came from a Quote, cancel or delete moves that quote back to Accepted. Linked Sales Orders recalculate remaining quantity (Open or Partially Recorded)",
  },
  {
    topic: "E-Invoice / E-Way Bill",
    rule: "Not on Draft or Cancelled invoices. Settings → GST portal credentials must be saved first — that login is what generates the IRN and e-way bill. You cannot generate an e-invoice while an e-way bill is already active. You cannot cancel the IRN while an e-way bill is active — cancel the e-way bill first",
  },
  {
    topic: "Apply Credit on the invoice detail",
    rule: "The Apply Credit button shows when this invoice still has a balance and the customer's Opening Balance on this invoice's branch is still negative (remaining credit). It opens Apply Opening Balance to Invoices with this invoice already selected and the branch locked. Those mappings appear on Payment History as Opening Balance — Credit; remove them before you can delete the invoice. The invoice list TOOLS menu also has Auto Map Opening Balance, which maps remaining credit across customers onto Sent / Partially Paid / Overdue invoices, oldest due date first",
  },
  {
    topic: "Attachments",
    rule: "Up to 3 files, 5 MB each. Editing a Draft adds new files to the existing ones; nothing is replaced unless you remove it",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating an invoice" },
      { heading: "2. Stock check and stock deduct — Inventory Config" },
      { heading: "3. Viewing, searching, and exporting" },
      { heading: "4. Editing an invoice" },
      { heading: "5. Printing and downloading" },
      { heading: "6. Changing an invoice's status" },
      { heading: "7. Converted from — what can become an Invoice" },
      { heading: "8. Converted to — what follows an Invoice" },
      { heading: "9. Apply Credit — opening-balance credit on this invoice" },
      { heading: "10. E-Invoice and E-Way Bill" },
      { heading: "11. Duplicating an invoice" },
      { heading: "12. Deleting an invoice" },
    ],
  },
  { heading: "Business rules" },
];

const FLOW = ["Quote", "Sales Order", "Invoice"];

const DeductPath = ({ title, subtitle, stockAt, recommended }) => (
  <div
    className="rounded-[12px] border border-[#E5E5E5] bg-white p-4"
    style={
      recommended
        ? { borderLeftWidth: 4, borderLeftColor: "#DF5D01" }
        : undefined
    }
  >
    <div className="flex items-start justify-between gap-3 mb-3">
      <div>
        <p className="text-[14px] sentient-medium text-[#111827] mb-0.5">
          {title}
        </p>
        <p className="text-[12px] geist-regular text-[#6B7280] leading-relaxed">
          {subtitle}
        </p>
      </div>
      {recommended && (
        <span className="text-[10px] geist-medium uppercase tracking-wide text-[#DF5D01] bg-[#FFF7F2] rounded-full px-2 py-0.5 flex-shrink-0">
          Default
        </span>
      )}
    </div>
    <div className="flex flex-col sm:flex-row sm:items-stretch gap-2">
      {FLOW.map((label, i) => {
        const leaves = label === stockAt;
        return (
          <div key={label} className="flex sm:flex-1 items-stretch gap-2 min-w-0">
            <div
              className={`flex-1 rounded-[10px] px-3 py-2.5 border ${
                leaves
                  ? "bg-[#FFF7F2] border-[#F4C7A8]"
                  : "bg-[#FAFAFA] border-[#E5E5E5]"
              }`}
            >
              <p className="text-[12px] geist-medium text-[#111827]">{label}</p>
              <p
                className={`text-[11px] geist-regular mt-0.5 ${
                  leaves ? "text-[#DF5D01]" : "text-[#6B7280]"
                }`}
              >
                {leaves ? "Stock leaves here" : "No stock move"}
              </p>
            </div>
            {i < FLOW.length - 1 && (
              <ArrowRight
                className="hidden sm:block w-3.5 h-3.5 text-[#D4D4D4] flex-shrink-0 self-center"
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </div>
  </div>
);

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function InvoicesPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Invoices
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone billing a customer for goods or services, then tracking
            what is still due
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            An <B>Invoice</B> is the bill — items, tax, due date, and balance.
            It can start from scratch, or carry forward an <B>Accepted
            Quote</B>, an <B>Open / Partially Recorded Sales Order</B>, or a{" "}
            <B>Sent Delivery Challan</B>. It posts to the ledger on save.
            Payments and credit notes settle the balance. When{" "}
            <B>Settings → Inventory Config → Stock Deduct Source</B> is
            Invoice, saving the invoice takes stock from the warehouse.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating an invoice
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Pick <B>Division</B>, then <B>Branch</B>, then <B>Warehouse</B> —
            the rest of the form stays locked until those three are set. A
            scratch invoice can pre-fill those three from{" "}
            <B>Settings → Organization defaults</B>. A few checks run
            automatically the moment you save.
          </p>
          <Definitions
            items={[
              {
                term: "Customer and Shipping Address",
                desc: (
                  <>
                    The address has to be{" "}
                    <B>one of that customer&apos;s own addresses</B>.
                  </>
                ),
              },
              {
                term: "Invoice Number",
                desc: (
                  <>
                    Auto-generated unless you turn that off under{" "}
                    <B>Invoice Configuration</B> on the form (prefix and next
                    number). That same drawer stores <B>Terms & Conditions</B>.
                    Locked after save.
                  </>
                ),
              },
              {
                term: "Due Date and Payment Terms",
                desc: (
                  <>
                    Terms come from the customer (or the source document). Due
                    Date is calculated from those terms and can be changed on
                    this invoice.
                  </>
                ),
              },
              {
                term: "Price List",
                desc: (
                  <>
                    Optional on a scratch invoice. Converted invoices already
                    have rates on the lines and the item list is locked.
                  </>
                ),
              },
              {
                term: "Currency and exchange rate",
                desc: (
                  <>
                    Currency comes from the customer, or from the Quote / Sales
                    Order / Delivery Challan if converted. If it differs from
                    the company base (set under <B>Settings → Currencies</B>),
                    an <B>Exchange Rate</B> field appears.
                  </>
                ),
              },
              {
                term: "Items, discounts, charges, and tax",
                desc: (
                  <>
                    Line items with quantity and rate. Document discount follows{" "}
                    <B>Settings → Discounts</B> — Enable Discount, then Discount
                    Applies On <B>Item Level</B> or <B>Transaction Level</B>.
                    Extra lines such as freight come from{" "}
                    <B>Settings → Additional charges</B> assigned to Invoice.
                    GST (CGST / SGST / IGST) from customer state vs company
                    state. Round-off is available on the totals.
                  </>
                ),
              },
              {
                term: "Credit limit and overdue invoices",
                desc: (
                  <>
                    Restriction Document (amount) and Restriction Payment Terms
                    Document (overdue invoice) come from the customer, or from{" "}
                    <B>Settings → Credit limits</B> until the customer overrides
                    them. Overdue check runs on create, not on edit.
                  </>
                ),
              },
              {
                term: "Stock check and stock deduct",
                desc: (
                  <>
                    Not a toggle on this form. They come from{" "}
                    <B>Settings → Inventory Config</B> (next section). If
                    Stock Deduct Source is Invoice, quantity is taken from the
                    warehouse on save — even for a Draft.
                  </>
                ),
              },
              {
                term: "Save vs Save & Sent",
                desc: (
                  <>
                    <B>Save</B> creates a Draft. <B>Save & Sent</B> is only on
                    create — saves and marks it <B>Sent</B> in one step.
                    Either way the invoice <B>posts to the ledger</B>.
                  </>
                ),
              },
            ]}
          />
          <Callout type="warning">
            You can&apos;t create an invoice at all until your company has a{" "}
            <B>primary bank account</B> set up. Mark one under Banking → Banks
            with <B>Make this primary</B>.
          </Callout>
          <Callout type="note">
            Unlike a Quote, Sales Order, or Delivery Challan, an invoice{" "}
            <B>does</B> post Accounts Receivable and Sales when you save —
            including as a Draft.
          </Callout>
          <Screenshot caption="Screenshot: the Add Invoice form, showing Division, Branch, Warehouse, Customer, Due Date, Save, and Save & Sent." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Stock check and stock deduct — Inventory Config
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Stock is not a toggle on every invoice the same way. It is a
            company setting under <B>Settings → Inventory Config</B> (screen
            title <B>Stock Source Configuration</B>). Two sales fields do
            different jobs. Check asks if quantity is there. Deduct is what
            actually takes it out of the warehouse.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <div className="border border-[#E5E5E5] rounded-[12px] p-4 bg-[#EFF8FF]">
              <p className="text-[12px] geist-medium uppercase tracking-wide text-[#1785B6] mb-1 flex items-center gap-1.5">
                <Search className="w-3.5 h-3.5" />
                Stock Check Source
              </p>
              <p className="text-[15px] sentient-medium text-[#111827] mb-1">
                Asks: is there enough?
              </p>
              <p className="text-[13px] geist-regular text-[#374151] leading-relaxed">
                Can sit on Quote, Sales Order, or Invoice. Blocks save if the
                warehouse is short. Does <B>not</B> take stock.
              </p>
            </div>
            <div className="border border-[#F4C7A8] rounded-[12px] p-4 bg-[#FFF7F2]">
              <p className="text-[12px] geist-medium uppercase tracking-wide text-[#DF5D01] mb-1 flex items-center gap-1.5">
                <PackageMinus className="w-3.5 h-3.5" />
                Stock Deduct Source
              </p>
              <p className="text-[15px] sentient-medium text-[#111827] mb-1">
                Takes stock out
              </p>
              <p className="text-[13px] geist-regular text-[#374151] leading-relaxed">
                Set to Invoice. The orange step below is where warehouse
                quantity actually leaves.
              </p>
            </div>
          </div>
          <div className="space-y-3 mb-4">
            <DeductPath
              title="Where stock leaves on an Invoice"
              subtitle="Save takes warehouse quantity — including a Draft."
              stockAt="Invoice"
            />
          </div>
          <Definitions
            items={[
              {
                term: "How it is used on save",
                desc: (
                  <>
                    At create, Books copies the current config onto the
                    invoice. If check or deduct is on for Invoice, availability
                    is validated. If deduct is Invoice, warehouse stock is
                    reduced and the Stock Movement tab shows those batches.
                    Editing a Draft reverses the old deduction and takes the
                    new quantities.
                  </>
                ),
              },
              {
                term: "How it is used on cancel / delete",
                desc: (
                  <>
                    If this invoice deducted stock, cancel or delete (when it
                    is not already Cancelled) puts the quantity back.
                  </>
                ),
              },
              {
                term: "Inventory Impact and Stock Movement",
                desc: (
                  <>
                    Detail shows whether Stock Check / Stock Deduct were on{" "}
                    <B>this</B> invoice at create time — the snapshot from when
                    it was created, not whatever the setting is today. Stock
                    Movement lists the batches if this invoice deducted.
                  </>
                ),
              },
              {
                term: "Changing the setting later",
                desc: (
                  <>
                    You cannot switch Stock Deduct Source <B>to</B> Invoice
                    while a Draft invoice exists. Changing Stock Check Source
                    alone is allowed. Already-saved invoices keep the snapshot
                    they were created with.
                  </>
                ),
              },
            ]}
          />
          <Callout type="warning">
            When Stock Deduct Source is Invoice, stock leaves when you save
            the invoice — even if you only Save as Draft.
          </Callout>
          <Screenshot caption="Screenshot: Settings → Inventory Config, Stock Source Configuration, with Stock Check Source and Stock Deduct Source set to Invoice." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Viewing, searching, and exporting
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The list and the detail page cover different needs.
          </p>
          <Definitions
            items={[
              {
                term: "Finding an invoice",
                desc: (
                  <>
                    Status tabs (Draft, Sent, Partially Paid, Paid, Overdue,
                    Cancelled), plus filters for <B>date range</B>,{" "}
                    <B>status</B>, <B>customer</B>, <B>added by</B>,{" "}
                    <B>division</B>, and <B>branch</B>. Search by invoice
                    number, reference, or customer name.
                  </>
                ),
              },
              {
                term: "Export",
                desc: (
                  <>
                    Export Invoices from the list toolbar, using the columns
                    you currently have showing.
                  </>
                ),
              },
              {
                term: "Detail page",
                desc: (
                  <>
                    Header shows invoice number, status, and balance.{" "}
                    <B>View Customer</B> opens that customer.{" "}
                    <B>Apply Credit</B> appears when this invoice still has a
                    balance and the customer has remaining opening-balance
                    credit on <B>this invoice&apos;s branch</B> — see section
                    9. Tabs cover the document, attachments,{" "}
                    <B>Document Flow</B>, <B>Stock Movement</B>, Payment
                    History (payments, credit notes, and Opening Balance —
                    Credit rows), and History. <B>Converted From</B> names the
                    Quote, Sales Order(s), or Delivery Challan(s).
                  </>
                ),
              },
              {
                term: "Auto Map Opening Balance (list)",
                desc: (
                  <>
                    Under TOOLS on the Invoices list. Maps remaining
                    opening-balance credit for customers onto Sent, Partially
                    Paid, and Overdue invoices, oldest due date first — not
                    limited to one customer. This is not a Payment Received.
                    The same action on a customer detail maps only that
                    customer.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Invoices list with status tabs and row actions." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Editing an invoice
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Only a <B>Draft</B> can be edited.
          </p>
          <Definitions
            items={[
              {
                term: "What stays locked even on a Draft",
                desc: (
                  <>
                    Customer, Division, Branch, Warehouse, and the invoice
                    number. Converted invoices also lock the item list to the
                    source lines.
                  </>
                ),
              },
              {
                term: "Stock on edit",
                desc: (
                  <>
                    If this invoice deducted stock, the previous deduction is
                    reversed and taken again for the updated quantities.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Edit on a Draft invoice, with customer and location fields disabled." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Printing and downloading
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            <B>Download</B> and <B>Print</B> sit on the detail header. When an
            e-invoice or e-way bill is active, those menus offer the plain
            invoice, with IRN / QR, with e-way bill, or e-way bill only.
          </p>
          <Screenshot caption="Screenshot: Download and Print on the invoice detail header, including e-invoice variants when an IRN exists." />

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Changing an invoice&apos;s status
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            <B>Mark Invoice As</B> only offers the moves below. Paid, Partially
            Paid, and Overdue are set by payments, credit notes, and the due
            date — not this menu.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Mark As offers
                  </th>
                </tr>
              </thead>
              <tbody>
                {TRANSITIONS.map((t, i) => (
                  <tr
                    key={t.from}
                    className={
                      i < TRANSITIONS.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[200px]">
                      {t.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {t.to}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Callout type="note">
            A <B>Cancelled</B> invoice cannot be un-cancelled. That is
            different from Quotes, Sales Orders, and Delivery Challans.
          </Callout>
          <Definitions
            items={[
              {
                term: "Cancelling",
                desc: (
                  <>
                    Blocked while a <B>non-cancelled</B> Payment Received or
                    Credit Note exists, or a Dispatch has been recorded. If
                    this invoice deducted stock, quantities go back to the
                    warehouse. The ledger journal is reversed.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Mark Invoice As on a Draft invoice, showing Sent and Cancelled." />

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Converted from — what can become an Invoice
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Conversion is started on the <B>source</B> document (or Duplicate /
            Add). Saving this invoice is what completes the source.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    When / where
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    What happens to the source
                  </th>
                </tr>
              </thead>
              <tbody>
                {CONVERT_FROM.map((row, i) => (
                  <tr
                    key={row.from}
                    className={
                      i < CONVERT_FROM.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[140px]">
                      {row.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.when}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Callout type="tip">
            A Customer Order converts only to a Sales Order. Convert that order
            to an Invoice afterwards. A Quote cannot become both a Sales Order
            and an Invoice.
          </Callout>
          <Screenshot caption="Screenshot: Converted From on an invoice detail, showing Quote, Sales Order, or Delivery Challan." />

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Converted to — what follows an Invoice
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            An invoice does not convert into another sales document. What
            follows is payment, credit, or dispatch.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    To
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    When
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Where you start
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    What happens
                  </th>
                </tr>
              </thead>
              <tbody>
                {CONVERT_TO.map((row, i) => (
                  <tr
                    key={row.to}
                    className={
                      i < CONVERT_TO.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[140px]">
                      {row.to}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.when}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.where}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Definitions
            items={[
              {
                term: "What you cannot convert this invoice into",
                desc: (
                  <>
                    Not a Quote, not a Sales Order, not a Delivery Challan, not
                    another Invoice. Those are separate documents.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Dispatch, Duplicate, Apply Credit, and Payment History on an invoice detail." />

          <h3
            id="section-0-sub-8"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            9. Apply Credit — opening-balance credit on this invoice
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            When the customer still has <B>negative Opening Balance</B>{" "}
            (credit) on <B>this invoice&apos;s branch</B>, and this invoice
            still has a balance, the detail header shows <B>Apply Credit</B>.
            That is an invoice-page action. It does not create a Payment
            Received. To pick a branch and map credit onto several invoices
            at once, use <B>Pay Against Invoices</B> on the customer detail.
          </p>
          <Definitions
            items={[
              {
                term: "When the button shows",
                desc: (
                  <>
                    Invoice balance greater than zero, customer Opening Balance
                    on this branch is still less than zero, you have Edit, and
                    the invoice is not view-only. If the credit is on a
                    different branch, this button stays hidden.
                  </>
                ),
              },
              {
                term: "Apply Opening Balance to Invoices",
                desc: (
                  <>
                    The modal is titled that. This invoice is already selected
                    and the branch is locked to this invoice&apos;s branch —
                    you do not pick a branch here. It shows Opening Payment #,
                    remaining credit, and a <B>Balance to Apply</B> field.
                    Total applied cannot exceed remaining credit. The switch{" "}
                    <B>Show current branch documents only</B> limits other
                    invoices in the list to this branch. The screen warns that
                    mapping is for Sent or Partially Paid invoices.
                  </>
                ),
              },
              {
                term: "What happens on save",
                desc: (
                  <>
                    Remaining opening-balance credit on that branch goes down.
                    This invoice becomes Partially Paid or Paid. Payment
                    History gains an <B>Opening Balance — Credit</B> row. You
                    can remove that row from Payment History; unused credit
                    comes back.
                  </>
                ),
              },
              {
                term: "Delete",
                desc: (
                  <>
                    Blocked while any Opening Balance — Credit row remains.
                    Remove those mappings first.
                  </>
                ),
              },
              {
                term: "Auto Map Opening Balance on the list",
                desc: (
                  <>
                    TOOLS → Auto Map Opening Balance. Company-wide for this
                    list: leftover credit onto Sent, Partially Paid, and
                    Overdue invoices, oldest due date first. Same kind of
                    mapping as Apply Credit, done in bulk. For one customer
                    only, use Auto Map on that customer&apos;s detail page.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Apply Credit on an invoice detail header, and the Apply Opening Balance to Invoices modal with remaining credit and Balance to Apply." />

          <h3
            id="section-0-sub-9"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            10. E-Invoice and E-Way Bill
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            These sit on the invoice detail when the matching plan feature is
            on, the invoice is not Draft or Cancelled, and{" "}
            <B>Settings → GST portal credentials</B> has a working portal login.
          </p>
          <Definitions
            items={[
              {
                term: "Generate E-Invoice",
                desc: (
                  <>
                    Creates an IRN using <B>Settings → GST portal credentials</B>.
                    Blocked while an e-way bill is already active on this
                    invoice — cancel that e-way bill first.
                  </>
                ),
              },
              {
                term: "Cancel E-Invoice",
                desc: (
                  <>
                    Blocked while an e-way bill is active. Cancel the e-way
                    bill, then cancel the IRN.
                  </>
                ),
              },
              {
                term: "E-Way Bill",
                desc: (
                  <>
                    Generate or cancel from the same side column. Some plans
                    have e-way bill without full e-invoice. Dispatch details
                    feed the e-way bill.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Generate E-Invoice, Generate E-Way Bill, and Cancel actions on a Sent invoice." />

          <h3
            id="section-0-sub-10"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            11. Duplicating an invoice
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            From the list or the detail page, <B>Duplicate Document</B> opens
            Add Invoice with the same customer, items, and amounts.
          </p>
          <Definitions
            items={[
              {
                term: "What is left off",
                desc: (
                  <>
                    A new invoice number, today&apos;s dates, Draft status, no
                    attachments, no source-document link, and no e-invoice or
                    e-way bill. Due date is recalculated from payment terms.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Duplicate Document on the invoice list row actions." />

          <h3
            id="section-0-sub-11"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            12. Deleting an invoice
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Delete is stricter than cancel.
          </p>
          <Definitions
            items={[
              {
                term: "Blocked when",
                desc: (
                  <>
                    Any <B>Payment Received</B>, any <B>Credit Note</B>, or a{" "}
                    <B>Dispatch</B> exists — including cancelled payments and
                    credit notes. Opening-balance credit applied on Payment
                    History must be removed first.
                  </>
                ),
              },
              {
                term: "Stock, ledger, and sources",
                desc: (
                  <>
                    If this invoice deducted stock and is not already
                    Cancelled, quantities go back. The ledger journal is
                    removed. A Quote used as the source returns to Accepted.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: a delete attempt blocked because a Payment Received exists from the invoice." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/sales/quotes"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Quotes <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              An Accepted quote can become this Invoice
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/delivery-challans"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Delivery Challans <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              A Sent challan converts only into an Invoice
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/payments-received"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Payments Received <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              How an invoice becomes Partially Paid or Paid
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/customer-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Customer <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where Opening Balance is set per branch, and Pay Against Invoices
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
